import { useEffect, useState } from 'react';

/**
 * Block 0 Phase 3 (ADR-0013 §6) — Combo standalone-detection.
 *
 * Hook возвращает `true`, если приложение запущено в standalone-режиме
 * (установлено как PWA и открыто из иконки на домашнем экране / app drawer).
 *
 * Ветви детекции (логическое OR):
 *
 * 1. `matchMedia('(display-mode: standalone)').matches` — современный путь
 *    (Android Chrome / Edge / Samsung Internet / Desktop Chrome / iOS 13+).
 *    Реактивный — слушаем `change` event, чтобы обновлять state, если
 *    пользователь переключает window mode (редкий desktop-кейс, но
 *    бесплатно покрываем).
 *
 * 2. `(navigator as Navigator & { standalone?: boolean }).standalone === true`
 *    — proprietary iOS Safari API (legacy, до iOS 16 — единственный
 *    надёжный сигнал). На Android/Desktop этого свойства нет, и проверка
 *    безопасна благодаря optional chaining через type intersection.
 *
 * Hook **не использует** `'ontouchstart' in window` или UA-эвристику —
 * это для определения «iOS Safari, который МОЖЕТ установить PWA вручную»
 * (Step 3 InstallScreen Container), но не для standalone state.
 */
export function useStandaloneMode(): boolean {
  const [standalone, setStandalone] = useState<boolean>(detectStandalone);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mql = window.matchMedia('(display-mode: standalone)');
    const handler = () => setStandalone(detectStandalone());

    mql.addEventListener('change', handler);
    // Re-sync after subscribe — закрывает гонку между initial useState и
    // монтированием listener'а (matchMedia мог измениться между ними).
    setStandalone(detectStandalone());

    return () => mql.removeEventListener('change', handler);
  }, []);

  return standalone;
}

function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  }
  if (typeof navigator !== 'undefined') {
    const nav = navigator as Navigator & { standalone?: boolean };
    if (nav.standalone === true) return true;
  }
  return false;
}
